/**
 * AccountMembersPage — the "Account & members" surface (T12.2 / prototype 👥).
 *
 * The home for identity, membership, and the household basics. Rehomes the
 * existing Household name / Members / Invites / Join / Switcher / Leave / Danger
 * Zone functionality (nothing lost), and adds the member-detail entry (age +
 * exclusions) by making each member row tappable → /settings/account/members/:id.
 *
 * Manager-only gating preserved: Invites + Danger Zone render only for managers,
 * exactly as the former single-page Settings did.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCurrentHousehold,
  useHouseholdUsers,
  useHouseholds,
  useMe,
} from '@shopista/shared/api/hooks';
import type { DiscoveredStore } from '@shopista/shared/types';
import { useAuthActions } from '../../auth/useAuthActions';
import { routes } from '../../routes/paths';
import { SettingsLayover } from './SettingsLayover';
import { SignInMethodsCard } from './SignInMethodsCard';
import {
  DangerZoneCard,
  HouseholdCard,
  InvitesCard,
  JoinHouseholdCard,
  MembersCard,
  SessionCard,
  SwitcherCard,
} from './cards';

export function AccountMembersPage(): JSX.Element {
  const meQuery = useMe();
  const householdQuery = useCurrentHousehold();
  const usersQuery = useHouseholdUsers();
  const householdsQuery = useHouseholds();
  const { logout } = useAuthActions();
  const navigate = useNavigate();

  // Discovery candidates aren't surfaced here (store-list management lives on
  // App settings); the StoresCard is rehomed there. Account & members keeps the
  // identity + membership ops only. `setState` is unused but kept for parity if
  // a future store affordance lands.
  const [, setDiscovered] = useState<DiscoveredStore[] | null>(null);
  void setDiscovered;

  const me = meQuery.data;
  const household = householdQuery.data;
  const users = usersQuery.data?.users ?? [];
  const households = householdsQuery.data ?? [];
  const currentMembership = households.find((h) => h.is_current);
  const isManager = currentMembership?.role === 'manager';

  return (
    <SettingsLayover
      emoji="👥"
      title="Account & members"
      backLabel="‹ Menu"
      testId="settings-account"
      band={{
        variant: 'steers',
        children: (
          <>
            Each member&apos;s <strong>age</strong> sharpens quantity scaling;
            their <strong>dietary exclusions</strong> filter suggestions.
          </>
        ),
      }}
    >
      <HouseholdCard
        householdLoading={householdQuery.isLoading}
        householdError={householdQuery.error}
        household={household}
        isManager={isManager}
      />

      {households.length > 1 && <SwitcherCard />}

      <MembersCard
        users={users}
        loading={usersQuery.isLoading}
        error={usersQuery.error}
        currentUserId={me?.id}
        isManager={isManager}
        onOpenMember={(userId) =>
          navigate(`${routes.settingsMemberDetail}/${userId}`)
        }
      />

      {isManager && <InvitesCard />}

      <JoinHouseholdCard />

      <SessionCard
        email={me?.email}
        onLogout={logout}
        canLeaveCurrent={!!household}
        householdName={household?.name}
      />

      {/* T27.2.10.1 — manage which methods (Google / password) sign in to this
          account, with the last-identity guard. */}
      <SignInMethodsCard />

      {isManager && household && (
        <DangerZoneCard
          householdId={household.id}
          householdName={household.name}
          onLogout={logout}
        />
      )}
    </SettingsLayover>
  );
}
